import { useTranslation } from "react-i18next";
import { Calendar, Plus, X } from "lucide-react";
import { Card, CardContent, Button, Input, Badge } from "@tabipla/ui";
import type { ConfirmDialogProps } from "@tabipla/components";
import { ConfirmDialog } from "@tabipla/components";
import type { SchedulePanelProps } from "./types";
import { ScheduleCardContainer } from "./ScheduleCardContainer";
import { formatDate } from "./utils";

interface SchedulePanelPresenterProps extends SchedulePanelProps {
  tripId: string;
  dialogProps: ConfirmDialogProps;
}

/**
 * SchedulePanel presenter component.
 * Pure presentation - receives all data and callbacks as props.
 * Does not contain any hooks for data fetching or mutations.
 */
export function SchedulePanel({
  tripId,
  schedules,
  isLoading,
  isCreating,
  newTitle,
  newDescription,
  newDateOptions,
  dateInput,
  expandedSchedules,
  isCreatingSchedule,
  isClosing,
  isDeleting,
  onNewTitleChange,
  onNewDescriptionChange,
  onDateInputChange,
  onAddDate,
  onRemoveDate,
  onCreate,
  onCancelCreate,
  onStartCreate,
  onClose,
  onDelete,
  onToggleExpand,
  dialogProps,
}: SchedulePanelPresenterProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <Card className="overflow-hidden">
        <CardContent className="p-4">
          {!isCreating ? (
            <Button
              onClick={onStartCreate}
              className="w-full rounded-xl py-6 text-base font-medium shadow-sm transition-all hover:shadow-md active:scale-[0.98]"
            >
              <Plus className="mr-2 h-5 w-5" />
              {t("schedules.createNew")}
            </Button>
          ) : (
            <form onSubmit={onCreate} className="space-y-4">
              <div>
                <Input
                  value={newTitle}
                  onChange={(e) => onNewTitleChange(e.target.value)}
                  placeholder={t("schedules.titlePlaceholder")}
                  className="font-medium"
                />
              </div>

              <div>
                <Input
                  value={newDescription}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onNewDescriptionChange(e.target.value)
                  }
                  placeholder={t("schedules.descriptionPlaceholder")}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("schedules.dateOptions")}</label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={dateInput}
                    onChange={(e) => onDateInputChange(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onAddDate}
                    disabled={!dateInput || newDateOptions.includes(dateInput)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {newDateOptions.map((date) => (
                    <Badge key={date} variant="secondary" className="gap-1 py-1">
                      {formatDate(date)}
                      <button
                        type="button"
                        onClick={() => onRemoveDate(date)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onCancelCreate} className="flex-1">
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={!newTitle.trim() || newDateOptions.length < 1 || isCreatingSchedule}
                  className="flex-1"
                >
                  {t("schedules.create")}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Schedules List */}
      {schedules.length === 0 ? (
        <Card className="overflow-hidden">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="font-medium text-muted-foreground">{t("schedules.noSchedules")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("schedules.noSchedulesDescription")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {schedules.map((schedule) => (
            <ScheduleCardContainer
              key={schedule.id}
              schedule={schedule}
              tripId={tripId}
              isExpanded={expandedSchedules.has(schedule.id)}
              onToggleExpand={() => onToggleExpand(schedule.id)}
              onClose={() => onClose(schedule.id)}
              onDelete={() => onDelete(schedule.id)}
              isClosing={isClosing}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      )}

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
